import React, { useContext, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";

import { KpiLayout } from "components/OverviewPages/KPIs/KpiLayout";
import Tooltip from "@mui/material/Tooltip";

import { useLazyFetchGet } from "hooks/fetch/useLazyFetchGet";
import { FeaturesContext } from "contexts/FeaturesContext";
import { EntitlementContext } from "contexts/UserProfileContext";

import { ReactComponent as MachineIcon } from "assets/img/KPIIcons/machines.svg";
import { MachineStatusIdLookup } from "core/enums";
import { useDownDerateTrackerData } from "contexts/DownDerateDataContext";

import { DEFAULT_SORT } from "components/DownDerateTrackerTable/DownDerateTrackerTableColumns";

import { styled } from "@mui/material/styles";

function getKeyByValue(object, value) {
  return Object.keys(object).find(key => object[key] === value);
}

export function MachineStatusKPI({ site, machine }) {
  const [machineStatusId, setMachineStatusId] = useState();

  const { setTableState } = useDownDerateTrackerData();

  const { hasFeature } = useContext(FeaturesContext);
  const { hasEntitlement } = useContext(EntitlementContext);
  const history = useHistory();

  const url = machine?.id ? `machines/${machine.id}/status` : `machines/status`;

  const { loading, getCallback } = useLazyFetchGet(
    url,
    machineStatusResponse => {
      setMachineStatusId(machineStatusResponse?.status);
    }
  );

  useEffect(() => {
    if (machine?.id) {
      getCallback();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [machine]);

  const onKpiClicked = () => {
    setTableState(state => ({
      ...state,
      sortBy: [DEFAULT_SORT],
      filters: {
        "Machine Status": [{ name: machineStatus, id: machineStatusId }],
        Site: site?.name && site?.id ? [{ name: site.name, id: site.id }] : [],
        Machine:
          machine?.name && machine?.id
            ? [{ name: machine.name, id: machine.id }]
            : []
      }
    }));
    history.push("/pages/down-derate-tracker");
  };

  const downDerateEnabled = useMemo(
    () =>
      hasFeature("down_derated_tracker") &&
      hasEntitlement("downderatedtracker-view"),
    [hasEntitlement, hasFeature]
  );

  const machineStatus = getKeyByValue(MachineStatusIdLookup, machineStatusId);

  return (
    <KpiLayout
      title="Machine Status"
      loading={loading || typeof machineStatusId !== "number"}
      Icon={MachineIcon}
      cursorEnable={downDerateEnabled}
      onClickEvent={downDerateEnabled ? onKpiClicked : undefined}
    >
      <Tooltip title="Machine Status" aria-label="down-derated-kpi-tooltip">
        <KpiBody>
          <KpiStatus>{machineStatus}</KpiStatus>
        </KpiBody>
      </Tooltip>
    </KpiLayout>
  );
}

const KpiBody = styled("div")`
  font-weight: 600;
  font-size: 32px;
  line-height: 48px;
  height: 48px;
`;

const KpiStatus = styled("span")`
  color: #485057;
`;

MachineStatusKPI.propTypes = {
  site: PropTypes.object,
  machine: PropTypes.object
};
